import { useState, useCallback, useEffect, useRef } from "react";
import InputMask from "react-input-mask";
import { useDropzone } from "react-dropzone";
import { IoClose } from "react-icons/io5";
import ProgressBar from "@ramonak/react-progress-bar";
import { Player } from "@lottiefiles/react-lottie-player";
import { v4 as uuidv4 } from "uuid";
import { cpf } from "cpf-cnpj-validator";

import SuccessAnimation from "../../../assets/animations/success.json";
import FileAnimation from "../../../assets/animations/file.json";

import api from "../../../services/api";

import {
	Container,
	Content,
	UploadMessage,
	DropContainer,
	UploadMessageContainer,
	FilesContainer,
	File,
} from "./styles";
import fileSize from "filesize";
import { notify } from "../../../components/Toast";
import { file } from "@babel/types";

interface IFile {
	file: File;
	progress: number;
	name: string;
	id: string;
	status: string;
	size: number;
	documentTagId: string;
}

interface IError {
	message?: string;
	empty: boolean;
}

function Files() {
	const [clientCpf, setClientCpf] = useState("");
	const [files, setFiles] = useState<IFile[]>([]);
	const [haveFiles, setHaveFiles] = useState(false);
	const cpfNumbersRef = useRef<{ value?: string }>({});
	const [error, setError] = useState<IError>({ empty: true });
	// const [sendedFiles, setSendedFiles] = useState<IFile[]>([]);

	const [fileAnimation, setFileAnimation] = useState<any>();

	const onDrop = useCallback((acceptedFiles: File[]) => {
		setHaveFiles(true);

		const filesAlreadyExists = acceptedFiles.filter((acceptedFile) => files.find((file) => file.name === acceptedFile.name));

		if(filesAlreadyExists.length > 0) {
			notify({
				type: "error",
				message: `Não foi possível adicionar os seguintes arquivos: ${filesAlreadyExists.map(file => file.name).join(', ')}.\nMotivo: Já existe na fila arquivos com esses nomes.`,
			});
			return;
		}

		const newFiles = acceptedFiles.map((file) => {
			return {
				file,
				progress: 0,
				name: file.name,
				id: uuidv4(),
				error: false,
				status: "pending",
				size: file.size,
				documentTagId: "",
			};
		});

		setFiles((state) => state.concat(newFiles));
	}, [files]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
	});

	useEffect(() => {
		if (fileAnimation) {
			if (!files.length) {
				fileAnimation.setDirection(-1);
				fileAnimation.play();

				return;
			}

			fileAnimation.setDirection(1);
			fileAnimation.play();
		}
	}, [isDragActive, files]);

	const renderDragMessage = useCallback(() => {
		return (
			<UploadMessageContainer>
				<Player
					src={FileAnimation}
					renderer='svg'
					//@ts-ignore
					lottieRef={(instance) => setFileAnimation(instance)}
					style={{ height: "100px", width: "100px" }}
					rendererSettings={{ preserveAspectRatio: "xMidYMin slice" }}
					speed={2}
					keepLastFrame
				/>
				{isDragActive ? (
					<UploadMessage type='default'>
						Arraste documentos aqui <br />
						ou <br />
						<span>Clique aqui</span>
					</UploadMessage>
				) : (
					<UploadMessage>
						Arraste documentos aqui <br />
						ou <br />
						<span>Clique aqui</span>
					</UploadMessage>
				)}
			</UploadMessageContainer>
		);
	}, [isDragActive]);

	const updateFile = useCallback((id, data) => {
		setFiles((state) =>
			state.map((file) => (file.id === id ? { ...file, ...data } : file)),
		);

		if (data.status === "uploaded") {
			setTimeout(() => {
				setFiles((state) =>
					state.filter((file) => file.status !== "uploaded"),
				);
			}, 3000);
		}
	}, []);

	const handleUpload = async () => {
		try {
			const documentsPromises = files.map(async (file, index: number) => {
				const formData = new FormData();

				formData.set("cpf", clientCpf);
				formData.set("documentTag", file.documentTagId);

				formData.set("file", file.file);

				updateFile(file.id, { status: "uploading" });

				return api.post("/process/documents", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
						"Access-Control-Allow-Origin": "*",
					},
					onUploadProgress: (progressEvent: any) => {
						const progress = Math.round(
							(progressEvent * 100) / progressEvent.total,
						);

						updateFile(file.id, { progress });
					},
				})
					.then(() => {
						updateFile(file.id, {
							status: "uploaded",
						});
					})
					.catch((err) => {
						updateFile(file.id, { status: "error" });
					});
			});

			await notify({promise: Promise.all(documentsPromises) , type: 'promise', message: {pending: 'Enviando documentos', success: 'Documentos enviados com sucesso!', error: 'Erro ao enviar documentos'}});
		} catch (error) {
			return error;
		}
	};

	return (
		<Container>
			<Content haveFiles={!!haveFiles}>
				<div className='header'>
					<h3>UPLOAD DE DOCUMENTOS</h3>

					<div className='description'>
						<p>
							Faça o upload dos documentos que serão necessarios
							para iniciar o processo do seu cliente.
						</p>
					</div>
				</div>

				<InputMask
					className={error?.message ? "error" : ""}
					mask='999.999.999-99'
					placeholder='Digite o CPF do seu cliente'
					autoFocus={true}
					onBlur={(e) => {
						setError((errorState) => {
							//Verifica se input está vazio
							if (!cpfNumbersRef.current.value) {
								return errorState?.message //Verifica se  tem mensagem de erro
									? { empty: true } //Retira a mensagem de erro
									: errorState.empty
									? errorState
									: { ...errorState, empty: true }; //Verifica o empty para ver se tinha texto antes e mudá-lo antes de retornar.
							}

							//Verifica se o cpf n atende ao formato

							if (!cpf.isValid(cpfNumbersRef.current.value)) {
								return errorState?.message
									? errorState
									: {
											message: "CPF não é valido",
											empty: false,
									  };
							}
							return { empty: false };
						});
					}}
					onChange={(e) => {
						setClientCpf((state) => {
							const cpfNumbers = e.target.value
								.split("")
								.filter(
									(n) =>
										!isNaN(parseFloat(n)) &&
										isFinite(Number(n)),
								)
								.join("");
							cpfNumbersRef.current.value = cpfNumbers;
							return e.target.value;
						});
					}}
					alwaysShowMask={false}
				/>
				{error?.message ? (
					<span className='error-message'>{error.message}</span>
				) : (
					""
				)}

				<DropContainer {...getRootProps()} active={!!isDragActive}>
					<input {...getInputProps()} name='file' />
					{renderDragMessage()}
				</DropContainer>

				<FilesContainer>
					{files.map((file, fileIndex) => {
						return (
							<File
								style={{
									animationDelay: `${fileIndex * 0.2}s`,
								}}
								progress={file.progress}
								key={fileIndex}>
								{file.status !== "uploaded" ? (
									<>
										<div className='up'>
											<div className='text-wrapper'>
												<p className='status'>
													{file.status === "uploading"
														? "Subindo..."
														: file.status ===
														  "error"
														? "Erro ao fazer upload."
														: "Esperando..."}
												</p>

												<div className='info'>
													<p className='progress'>
														{file.progress}% ● 3
														segundos
													</p>
													<p>{file.name}</p>
													<p title={file.name}>
														{fileSize(file.size)}
													</p>
												</div>
											</div>

											<div className='buttons-wrapper'>
												<button
													className='cancel'
													onClick={() => {
														setFiles((state) =>
															state.filter(
																(
																	item,
																	index,
																) => {
																	return (
																		fileIndex !==
																		index
																	);
																},
															),
														);
													}}>
													<IoClose size={25} />
												</button>
											</div>
										</div>

										<div className='progressBar-wrapper'>
											<ProgressBar
												completed={file?.progress}
												height='4px'
												width='100%'
												isLabelVisible={false}
												bgColor='#3751ff'
												baseBgColor='none'
											/>
										</div>
									</>
								) : (
									<div className='completed'>
										<p>Upload completo!</p>
										<Player
											src={SuccessAnimation}
											renderer='svg'
											style={{
												height: "40px",
												width: "40px",
											}}
											rendererSettings={{
												preserveAspectRatio:
													"xMidYMin slice",
											}}
											speed={1.5}
											keepLastFrame
											autoplay
										/>
									</div>
								)}
							</File>
						);
					})}
				</FilesContainer>

				<div
					className={`confirm ${
						files.length && !error?.message && !error.empty
							? "enabled"
							: "disabled"
					}`}>
					<button disabled = {files.length && !error?.message && !error.empty
							? false
							: true} onClick={handleUpload}>Enviar</button>
				</div>
			</Content>
		</Container>
	);
}
export default Files;
